import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemMultiTextValue } from '@/common/api/types/items';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import type { MultiValueFieldValue } from './MultiValueFieldManager';
import { MultiValueFieldManager } from './MultiValueFieldManager';
import { TextEditDialog } from './TextEditDialog';
import { MULTI_TEXT_VALUE_MAX_LENGTH, MULTI_VALUE_MAX_COUNT } from './validation';

interface MultiTextFieldProps {
  id: string;
  name: string;
  values?: ItemMultiTextValue[];
  isEditable?: boolean;
  autoFocus?: boolean;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  onChange: (values: string[]) => void;
  onValidationChange?: (error: string | null) => void;
}

export const MultiTextField = ({
  name,
  values = [],
  isEditable = false,
  autoFocus = false,
  isPending = false,
  isSuccess = false,
  isError,
  error,
  onChange,
  onValidationChange,
}: MultiTextFieldProps) => {
  const { t } = useTranslation();
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(autoFocus && values.length === 0);
  const [editingValueId, setEditingValueId] = React.useState<number | null>(null);
  const [editingValue, setEditingValue] = React.useState<string | undefined>(undefined);

  const handleValidationError = (err: string | null) => {
    setValidationError(err);
    onValidationChange?.(err);
  };

  const handleOnAdd = (text: string) => {
    if (text.length > MULTI_TEXT_VALUE_MAX_LENGTH) {
      handleValidationError(
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiTextValueMaxLength, {
          maxLength: MULTI_TEXT_VALUE_MAX_LENGTH,
        }),
      );
      return;
    }
    handleValidationError(null);
    onChange([...values.map((v) => v.MULTI_TEXT), text]);
  };

  const handleOnUpdate = (valueId: number, text: string) => {
    if (text.length > MULTI_TEXT_VALUE_MAX_LENGTH) {
      handleValidationError(
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiTextValueMaxLength, {
          maxLength: MULTI_TEXT_VALUE_MAX_LENGTH,
        }),
      );
      return;
    }
    handleValidationError(null);
    onChange(values.map((v) => (v.id === valueId ? text : v.MULTI_TEXT)));
  };

  const handleAddClick = () => {
    if (isPending) return;
    setEditingValueId(null);
    setEditingValue(undefined);
    setDialogOpen(true);
  };

  const handleEditClick = (id: number, text: string) => {
    setEditingValueId(id);
    setEditingValue(text);
    setDialogOpen(true);
  };

  const handleSave = (text: string) => {
    if (editingValueId === null) {
      handleOnAdd(text);
    } else {
      handleOnUpdate(editingValueId, text);
    }
    setDialogOpen(false);
    setEditingValueId(null);
  };

  const fieldValues: MultiValueFieldValue<string>[] = values.map((v) => ({
    id: v.id,
    value: v.MULTI_TEXT,
  }));

  const resolvedIsError = !!validationError || !!isError;
  const resolvedError = validationError ? new Error(validationError) : (error ?? null);

  return (
    <FieldWithStatusIndicator name={name} isPending={isPending} isSuccess={isSuccess}>
      <div className="flex flex-col gap-1">
        <MultiValueFieldManager
          values={fieldValues}
          isEditable={isEditable}
          disabled={isPending}
          onRemove={(id) => onChange(values.filter((v) => v.id !== id).map((v) => v.MULTI_TEXT))}
          maxCount={MULTI_VALUE_MAX_COUNT}
          onAddClick={handleAddClick}
        >
          {(fieldValue) => {
            if (!isEditable) {
              return <span>{fieldValue.value}</span>;
            }
            return (
              <button
                type="button"
                className="w-full text-left text-sm"
                onClick={() =>
                  fieldValue.id !== undefined && handleEditClick(fieldValue.id, fieldValue.value)
                }
                disabled={isPending}
              >
                {fieldValue.value}
              </button>
            );
          }}
        </MultiValueFieldManager>
        {resolvedIsError && resolvedError && (
          <span className="text-xs text-text-error">{resolvedError.message}</span>
        )}
      </div>

      <TextEditDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingValueId(null);
        }}
        initialValue={editingValue}
        onSave={handleSave}
        mode={editingValueId === null ? 'add' : 'edit'}
      />
    </FieldWithStatusIndicator>
  );
};
